import React, { FC, memo } from 'react';
import { FaRegImage } from 'react-icons/fa6';
import { IoDocumentOutline } from 'react-icons/io5';
import { MdOutlineSmartDisplay } from 'react-icons/md';
import cls from './ChatFileItem.module.scss';
import { classNameMerge } from '../../../utils/classNameMerge';
import { convertBinaryToLink } from '../../../utils/converBinaryToLink';
import { FileCategory, getFileCategory, getFileLabel } from '../../../utils/fileTypeView';
import routes from '../../../routes';

type ChatFileItemProps = {
  file: {
    id?: string;
    fullName: string;
    type: string;
    size: number;
  };
  isConsumer: boolean;
};

const MessageIcon = memo(({ category }: { category: FileCategory }) => {
  switch (category) {
    case 'image':
      return <FaRegImage size="1.15rem" />;
    case 'video':
      return <MdOutlineSmartDisplay size="1.15rem" />;
    default:
      return <IoDocumentOutline size="1.15rem" />;
  }
});

const ChatFileItem: FC<ChatFileItemProps> = memo((props) => {
  const { file, isConsumer } = props;
  const fileCategory = getFileCategory(file.type, file.fullName);
  const fileLabel = getFileLabel(file.fullName, file.type);
  const downloadHandler = () => {
    if (!file.id) {
      return;
    }
    const fileUrl = `${routes.api.basePath()}${routes.api.getMessageFileById(file.id)}`;
    // the message already carries the real name, so a mangled header cannot rename the file
    convertBinaryToLink(fileUrl, undefined, file.fullName);
  };
  const convertSize = (size: number) => {
    if (size < 1024) {
      return `${size} B`;
    } else if (size >= 1024 && size < 1024 * 1024) {
      return `${(size / 1024).toFixed(2)} KB`;
    } else if (size >= 1024 * 1024) {
      return `${(size / 1024 / 1024).toFixed(2)} MB`;
    }
  };
  const setFileContainerClass = (isConsumer: boolean) => {
    if (isConsumer) {
      return `${cls.File} ${cls.ConsumerFile}`;
    } else {
      return `${cls.File}`;
    }
  };
  if (!file.id)
    return (
      <div className={classNameMerge(`${setFileContainerClass(isConsumer)}`, cls.DeletedFile)}>
        <div className={`${cls.Img} ${cls.DeletedImg}`}>
          <MessageIcon category={fileCategory} />
        </div>
        <div>
          <p className={`${cls.FileName} ${cls.DeleteFileName}`}>{file.fullName}</p>
          <div className={cls.Size}>
            <span className={cls.DeletedFileType}>{fileLabel}</span>
            <span>File deleted</span>
          </div>
        </div>
      </div>
    );
  return (
    <div className={setFileContainerClass(isConsumer)} onClick={downloadHandler}>
      <div className={cls.Img}>
        <MessageIcon category={fileCategory} />
      </div>
      <div>
        <p className={cls.FileName}>{file.fullName}</p>
        <div className={cls.Size}>
          <span>{fileLabel}</span>
          <span>{convertSize(file.size)}</span>
        </div>
      </div>
    </div>
  );
});

export default ChatFileItem;
